import { useState } from 'react';
import { Smartphone, Mail, CheckCircle2, ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * iOS launch waitlist banner — placed at the top of the landing page.
 *
 * Submits to POST /api/waitlist/ios. The backend is idempotent (re-submitting
 * the same email returns `already=true`), so we show the same "you're on the
 * list" success state either way to keep things simple.
 */
export const IOSWaitlistBanner = () => {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [signedUp, setSignedUp] = useState(false);
  const [error, setError] = useState('');

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trimmed)) {
      setError('Enter a valid email address.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/waitlist/ios`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmed, source: 'landing_page_hero' }),
      });
      if (!res.ok) throw new Error('signup_failed');
      setSignedUp(true);
    } catch (err) {
      setError("Couldn't save — try again or refresh the page.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="ios-waitlist-banner"
      className="relative overflow-hidden rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/10 via-emerald-500/5 to-transparent p-5 sm:p-6 mb-8"
    >
      {/* subtle backdrop glow */}
      <div className="pointer-events-none absolute -right-16 -top-16 w-56 h-56 bg-emerald-500/20 blur-3xl rounded-full" />

      <div className="relative flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-6">
        {/* Left — pitch */}
        <div className="flex items-start gap-3 lg:flex-1">
          <div className="hidden sm:flex shrink-0 w-11 h-11 rounded-xl bg-emerald-500/15 border border-emerald-500/40 items-center justify-center">
            <Smartphone className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
              </span>
              <span className="text-[10px] sm:text-[11px] font-black uppercase tracking-[0.18em] text-emerald-400">
                iOS App · Submitted to App Store
              </span>
            </div>
            <h3 className="text-base sm:text-lg font-extrabold text-white leading-snug">
              Be the first to install SmartKick on iPhone
            </h3>
            <p className="text-xs sm:text-[13px] text-zinc-400 mt-1 leading-relaxed">
              We're in Apple's review queue — expected to go live within days. Drop your email and we'll send you a one-tap install link the moment it's approved.
            </p>
          </div>
        </div>

        {/* Right — form / confirmation */}
        <div className="lg:w-[360px] lg:shrink-0">
          {signedUp ? (
            <div
              className="flex items-center gap-2 px-4 py-3 rounded-xl bg-emerald-500/15 border border-emerald-500/40"
              data-testid="ios-waitlist-success"
            >
              <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
              <div className="text-sm">
                <div className="font-semibold text-emerald-300">You're on the list</div>
                <div className="text-[12px] text-zinc-400">Check your inbox — we just sent a confirmation.</div>
              </div>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="flex flex-col gap-2" data-testid="ios-waitlist-form">
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="relative flex-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <input
                    type="email"
                    required
                    autoComplete="email"
                    inputMode="email"
                    placeholder="you@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={submitting}
                    data-testid="ios-waitlist-email-input"
                    className="w-full bg-black/40 border border-white/10 focus:border-emerald-500/60 rounded-xl pl-9 pr-3 py-3 text-sm text-white placeholder:text-zinc-500 outline-none transition-colors"
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="ios-waitlist-submit"
                  className="px-4 py-3 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold text-sm rounded-xl transition-all flex items-center justify-center gap-1.5 whitespace-nowrap shadow-[0_0_18px_rgba(16,185,129,0.45)]"
                >
                  {submitting ? 'Saving…' : 'Notify me'}
                  {!submitting && <ArrowRight className="w-4 h-4" />}
                </button>
              </div>
              {error && (
                <p className="text-[11px] text-rose-400" data-testid="ios-waitlist-error">{error}</p>
              )}
              <p className="text-[10px] text-zinc-500">
                One email when we launch. You'll also get our weekly tips digest — unsubscribe any time.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default IOSWaitlistBanner;
